import React, { type FC, useEffect, useMemo, useState } from 'react';
import {
	FormattedMessage,
	injectIntl,
	type WithIntlProps,
	type WrappedComponentProps,
} from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield/text-field';
import IconButton from '@atlaskit/button/icon/button';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import Lozenge from '@atlaskit/lozenge/lozenge';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import SearchIcon from '@atlaskit/icon/core/search';
import AddIcon from '@atlaskit/icon/core/add';
import DeleteIcon from '@atlaskit/icon/core/delete';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';

import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import { CHANGES_REQUESTED_STATUS_ID } from '../../SpaceAdminConfig/StatusAndApprovalsConfigShared.ts';
import type { ContentState } from '../types.ts';

import type { DefaultSectionItem } from './ContentStateMenuV2.tsx';
import { i18n } from './contentStateMenuV2I18n.ts';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	row: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		justifyContent: 'space-between',
	},
	searchInput: {
		paddingInline: token('space.150'),
		paddingTop: token('space.100'),
	},
	after: {
		paddingInlineEnd: token('space.075'),
	},
	workflowStatusBadge: {
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		gap: token('space.100'),
		alignItems: 'center',
	},
	workflowStatusLabel: {
		alignItems: 'center',
		gap: token('space.100'),
	},
	badgeWrapperStyles: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		borderRadius: token('radius.xsmall'),
	},
	iconStyles: {
		marginLeft: token('space.negative.025'),
	},
	customRow: {
		position: 'relative',
	},
	moreActionsButton: {
		position: 'absolute',
		top: '50%',
		right: token('space.100'),
		transform: 'translateY(-50%)',
		zIndex: 1,
	},
	noStatusFound: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		textAlign: 'center',
	},
});

type StatusOptionRowProps = {
	option: ContentState;
	onSelect: (status: ContentState) => void;
	isActive?: boolean;
	onDelete?: (status: ContentState) => void;
	deleteLabel?: string;
	moreActionsLabel?: string;
};

const StatusOptionRow: FC<StatusOptionRowProps> = ({
	option,
	onSelect,
	isActive,
	onDelete,
	deleteLabel,
	moreActionsLabel,
}) => {
	const handleClick = () => {
		if (option && option.name) {
			onSelect(option);
		}
	};

	// Only show the delete affordance for custom statuses when a delete handler
	// is provided and the approvals beta is enabled. Gate is evaluated last per
	// the gate-last ordering rule.
	const showDelete =
		Boolean(onDelete) && expValEquals('confluence_approvals_eap', 'isEnabled', true);

	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleDelete = () => {
		setIsMenuOpen(false);
		onDelete?.(option);
	};

	useEffect(() => {
		if (!isMenuOpen) {
			return;
		}
		const handleScroll = () => setIsMenuOpen(false);
		window.addEventListener('scroll', handleScroll, true);
		return () => window.removeEventListener('scroll', handleScroll, true);
	}, [isMenuOpen]);

	const optionKey = option.id ?? option.name ?? 'unknown';

	const buttonItem = (
		<ButtonItem
			isSelected={isActive}
			iconBefore={
				<ContentStateColor
					color={option.color}
					restrictionLevel={option.restrictionLevel}
					appearance="contentStateButton"
				/>
			}
			onClick={handleClick}
		>
			{option.name}
		</ButtonItem>
	);

	if (!showDelete) {
		return buttonItem;
	}

	// Render the more-actions control as a sibling of the selectable ButtonItem
	// (not a descendant) to avoid nesting interactive elements
	// (`nested-interactive` a11y violation). It is absolutely positioned over the
	// right edge of the row and opens a dropdown with a single "Delete" action.
	return (
		<Box xcss={styles.customRow}>
			{buttonItem}
			<Box xcss={styles.moreActionsButton}>
				<DropdownMenu<HTMLButtonElement>
					placement="bottom-end"
					isOpen={isMenuOpen}
					onOpenChange={({ isOpen }) => setIsMenuOpen(isOpen)}
					trigger={({ triggerRef, ...triggerProps }) => (
						<IconButton
							{...triggerProps}
							ref={triggerRef}
							appearance="subtle"
							spacing="compact"
							icon={ShowMoreHorizontalIcon}
							label={moreActionsLabel ?? ''}
							testId={`custom-status-more-${optionKey}`}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem
							elemBefore={<DeleteIcon label="" color={token('color.icon.danger')} />}
							onClick={handleDelete}
							testId={`delete-custom-status-${optionKey}`}
						>
							<Text color="color.text.danger">{deleteLabel ?? ''}</Text>
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			</Box>
		</Box>
	);
};

type WorkflowStatusOptionRowProps = {
	status: WorkflowStatus;
	onSelect: (status: WorkflowStatus) => void;
};

const WorkflowStatusOptionRow: FC<WorkflowStatusOptionRowProps> = ({ status, onSelect }) => {
	const handleClick = () => onSelect(status);
	const Icon =
		status.id === CHANGES_REQUESTED_STATUS_ID
			? StatusWorkflowReturnedIcon
			: StatusWorkflowInReviewIcon;

	return (
		<ButtonItem
			iconBefore={
				<Box xcss={styles.iconStyles}>
					<Icon label="" size="small" color={token('color.icon.accent.blue')} spacing="none" />
				</Box>
			}
			onClick={handleClick}
			testId={`workflow-status-option-${status.id}`}
		>
			<Inline xcss={styles.row}>
				<Inline xcss={styles.workflowStatusLabel}>
					{status.allowsReviews ? <FormattedMessage {...i18n.startApproval} /> : status.name}
					{status.allowsReviews && (
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.approvalBetaTag} />
						</Lozenge>
					)}
				</Inline>
				{status.allowsReviews ? (
					<ChevronRightIcon size="small" label="" />
				) : (
					<Inline xcss={styles.workflowStatusBadge}>
						<Box xcss={styles.badgeWrapperStyles}>
							<Text size="small" color="color.text.subtlest">
								<FormattedMessage {...i18n.startApproval} />
							</Text>
						</Box>

						<ChevronRightIcon size="small" label="" />
					</Inline>
				)}
			</Inline>
		</ButtonItem>
	);
};

type ApprovalUpsellOptionRowProps = {
	onSelect: () => void;
};

const ApprovalUpsellOptionRow: FC<ApprovalUpsellOptionRowProps> = ({ onSelect }) => {
	return (
		<ButtonItem
			iconBefore={
				<Box xcss={styles.iconStyles}>
					<StatusWorkflowInReviewIcon
						label=""
						size="small"
						color={token('color.icon.accent.blue')}
						spacing="none"
					/>
				</Box>
			}
			onClick={onSelect}
			testId="approval-upsell-option"
		>
			<Inline xcss={styles.row}>
				<Inline xcss={styles.workflowStatusLabel}>
					<FormattedMessage {...i18n.startApproval} />
					<Lozenge appearance="discovery">
						<FormattedMessage {...i18n.approvalBetaTag} />
					</Lozenge>
				</Inline>
				<ChevronRightIcon size="small" label="" />
			</Inline>
		</ButtonItem>
	);
};

type ContentStateMenuV2SelectViewProps = WrappedComponentProps & {
	searchValue: string;
	onSearchChange: (value: string) => void;
	filteredDefaultSection: DefaultSectionItem[];
	filteredCustomOptions: ContentState[];
	hasResults: boolean;
	isCustomEnabled: boolean;
	currentContentState: ContentState | null;
	onSelectStatus: (status: ContentState) => void;
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
	onSelectApprovalsUpsell: () => void;
	onAddCustomClick: () => void;
	onDeleteCustomStatus?: (status: ContentState) => void;
};

const ContentStateMenuV2SelectViewComponent: FC<ContentStateMenuV2SelectViewProps> = ({
	searchValue,
	onSearchChange,
	filteredDefaultSection,
	filteredCustomOptions,
	hasResults,
	isCustomEnabled,
	currentContentState,
	onSelectStatus,
	onSelectWorkflowStatus,
	onSelectApprovalsUpsell,
	onAddCustomClick,
	onDeleteCustomStatus,
	intl: { formatMessage },
}) => {
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onSearchChange(e.currentTarget.value);

	const { nonApprovalDefaultRows, approvalRow } = useMemo(() => {
		const nonApproval: DefaultSectionItem[] = [];
		let approval: DefaultSectionItem | undefined;
		for (const item of filteredDefaultSection) {
			if (
				((item.type === 'workflowStatus' && item.workflowStatus.allowsReviews) ||
					item.type === 'approvalUpsell') &&
				approval === undefined
			) {
				approval = item;
			} else {
				nonApproval.push(item);
			}
		}
		return { nonApprovalDefaultRows: nonApproval, approvalRow: approval };
	}, [filteredDefaultSection]);

	const showCustomSection =
		filteredCustomOptions.length > 0 &&
		(isCustomEnabled || !expValEquals('confluence_approvals_eap', 'isEnabled', true));

	// Section titles only render when there are custom statuses to disambiguate from.
	const defaultSectionTitle = showCustomSection ? formatMessage(i18n.defaultWorkflow) : undefined;

	const renderDefaultSectionItem = (item: DefaultSectionItem) => {
		if (item.type === 'contentState') {
			return (
				<StatusOptionRow
					key={item.contentState.id}
					option={item.contentState}
					onSelect={onSelectStatus}
					isActive={currentContentState?.id === item.contentState.id}
				/>
			);
		}

		if (item.type === 'workflowStatus') {
			return (
				<WorkflowStatusOptionRow
					key={`workflow-${item.workflowStatus.id}`}
					status={item.workflowStatus}
					onSelect={onSelectWorkflowStatus}
				/>
			);
		}

		return <ApprovalUpsellOptionRow key="approval-upsell" onSelect={onSelectApprovalsUpsell} />;
	};

	return (
		<Stack xcss={styles.dialog} testId="status-selector-popup-menu">
			<MenuGroup maxHeight={360}>
				<Section>
					<Inline xcss={styles.searchInput}>
						<Textfield
							name="status-search"
							aria-label={formatMessage(i18n.searchPlaceholder)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							maxLength={20}
							isCompact
							value={searchValue}
							onChange={handleSearchChange}
							autoComplete={
								expValEquals('confluence_approvals_eap', 'isEnabled', true) ? 'off' : undefined
							}
							elemAfterInput={
								<Box xcss={styles.after}>
									<SearchIcon label="" />
								</Box>
							}
							testId="status-search-input"
						/>
					</Inline>
				</Section>
				<Section isScrollable>
					{nonApprovalDefaultRows.length > 0 && (
						<Section title={defaultSectionTitle}>
							<MenuGroup>{nonApprovalDefaultRows.map(renderDefaultSectionItem)}</MenuGroup>
						</Section>
					)}
					{showCustomSection && (
						<Section title={formatMessage(i18n.customStatuses)}>
							{filteredCustomOptions.map((option) => (
								<StatusOptionRow
									key={option.id}
									option={option}
									onSelect={onSelectStatus}
									isActive={currentContentState?.id === option.id}
									onDelete={onDeleteCustomStatus}
									deleteLabel={formatMessage(i18n.delete)}
									moreActionsLabel={formatMessage(i18n.moreActions, {
										statusName: option.name ?? '',
									})}
								/>
							))}
						</Section>
					)}
					{approvalRow && approvalRow.type === 'workflowStatus' && (
						<Section hasSeparator={nonApprovalDefaultRows.length > 0 || showCustomSection}>
							<WorkflowStatusOptionRow
								key={`workflow-${approvalRow.workflowStatus.id}`}
								status={approvalRow.workflowStatus}
								onSelect={onSelectWorkflowStatus}
							/>
						</Section>
					)}
					{approvalRow && approvalRow.type === 'approvalUpsell' && (
						<Section hasSeparator={nonApprovalDefaultRows.length > 0 || showCustomSection}>
							<ApprovalUpsellOptionRow onSelect={onSelectApprovalsUpsell} />
						</Section>
					)}
					{!hasResults &&
						searchValue.trim() &&
						(!isCustomEnabled && expValEquals('confluence_approvals_eap', 'isEnabled', true) ? (
							<Section>
								<Box xcss={styles.noStatusFound} testId="no-status-found" role="status">
									<Text color="color.text.subtlest">{formatMessage(i18n.noStatusFound)}</Text>
								</Box>
							</Section>
						) : (
							<Section>
								<ButtonItem
									iconBefore={<AddIcon label="" />}
									description={formatMessage(i18n.addCustomStatusDescription)}
									onClick={onAddCustomClick}
									testId="add-custom-status-button"
								>
									{formatMessage(i18n.addCustomStatus)}
								</ButtonItem>
							</Section>
						))}
				</Section>
			</MenuGroup>
		</Stack>
	);
};

export const ContentStateMenuV2SelectView: React.FC<
	WithIntlProps<ContentStateMenuV2SelectViewProps>
> & {
	WrappedComponent: React.ComponentType<ContentStateMenuV2SelectViewProps>;
} = injectIntl(ContentStateMenuV2SelectViewComponent);
